import { createContext, useContext, useState, useMemo, useCallback } from 'react';

const AgentContext = createContext(null);

// vista#595: persist the selected agent across reloads. Without this the
// store was memory-only (`useState(null)`), so a refresh reset agentId to
// null and Header.jsx re-defaulted to the first agent — bouncing the operator
// off whatever agent they were viewing. We stash it in localStorage and
// restore on init. Header.jsx already validates the restored id against the
// live /agents list and falls back to the first agent if it's stale (agent
// removed / different org), so a bad stored value can't strand the UI.
export const AGENT_STORAGE_KEY = 'vista.selectedAgentId';

// SSR- and private-mode-safe localStorage access (never throws).
export function readStoredAgentId() {
  try {
    return localStorage.getItem(AGENT_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function persistAgentId(id) {
  try {
    if (id) localStorage.setItem(AGENT_STORAGE_KEY, id);
    else localStorage.removeItem(AGENT_STORAGE_KEY);
  } catch {
    // Storage unavailable (SSR / disabled / private mode) — selection still
    // works for the current session, it just won't survive a reload.
  }
}

export function AgentProvider({ children }) {
  // Lazy init from storage so the first render already reflects the
  // previously-selected agent (no null → default flash on reload).
  const [agentId, setAgentIdState] = useState(readStoredAgentId);

  const setAgentId = useCallback((id) => {
    setAgentIdState(id);
    persistAgentId(id);
  }, []);

  const value = useMemo(() => ({ agentId, setAgentId }), [agentId, setAgentId]);

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
}

export function useAgentStore() {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgentStore must be used within an AgentProvider');
  }
  return context;
}
